'use client';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { AppLink } from '@/lib/appLink';

/**
 * Linked Section video — SSR + ThemeScript (play/mute without React hydration).
 * @param {{ videoUrl?: string }} props
 */
function SectionVideo({ videoUrl }) {
  if (!videoUrl) return null;

  return (
    <div
      className="grv-linked-video relative rounded-2xl overflow-hidden bg-dark w-full max-h-[850px] shadow-2xl shadow-black/40 group"
      style={{ aspectRatio: '9/16' }}
      data-grv-linked-video
    >
      <video
        data-grv-linked-video-el
        src={videoUrl}
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
      />

      {/* Center play — visible while paused */}
      <button
        type="button"
        data-grv-linked-video-play
        className="grv-linked-video-play absolute inset-0 z-10 flex items-center justify-center cursor-pointer"
        aria-label="Відтворити відео"
      >
        <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gold/90 border border-gold flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110">
          <Play className="w-7 h-7 sm:w-8 sm:h-8 text-dark fill-dark ml-1 pointer-events-none" />
        </span>
      </button>

      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
      <div
        data-grv-linked-video-progress
        className="absolute bottom-16 left-4 right-4 h-1 bg-white/20 rounded-full cursor-pointer hover:h-1.5 transition-all z-20"
      >
        <div
          data-grv-linked-video-progress-fill
          className="h-full bg-gold rounded-full"
          style={{ width: '0%' }}
        />
      </div>
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-20">
        <button
          type="button"
          data-grv-linked-video-toggle
          className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors"
          aria-label="Play / Pause"
        >
          <Play data-icon="play" className="w-4 h-4 ml-0.5" />
          <Pause data-icon="pause" className="w-4 h-4" hidden />
        </button>
        <button
          type="button"
          data-grv-linked-video-mute
          className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors"
          aria-label="Mute"
        >
          <VolumeX data-icon="muted" className="w-4 h-4" />
          <Volume2 data-icon="unmuted" className="w-4 h-4" hidden />
        </button>
      </div>
      <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-gold rounded-tl-2xl pointer-events-none" />
    </div>
  );
}

function SectionGallery({ images = [] }) {
  if (!images.length) return null;

  return (
    <div
      className="grv-gallery relative"
      data-grv-gallery
      data-grv-gallery-index="0"
      data-grv-gallery-count={String(images.length)}
    >
      <div
        className="relative rounded-xl overflow-hidden bg-card border border-border cursor-pointer"
        style={{ aspectRatio: '4/3' }}
      >
        {images.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt=""
            data-grv-gallery-slide
            data-grv-gallery-open
            data-index={i}
            data-src={src}
            className={`grv-gallery-slide absolute inset-0 w-full h-full object-cover transition-opacity duration-500 cursor-pointer ${
              i === 0 ? 'is-active' : ''
            }`}
          />
        ))}
        {images.length > 1 ? (
          <>
            <button
              type="button"
              data-grv-gallery-prev
              aria-label="Попереднє фото"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-dark/60 backdrop-blur-sm text-white hover:text-gold border border-white/20 hover:border-gold flex items-center justify-center transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-5 h-5 pointer-events-none" />
            </button>
            <button
              type="button"
              data-grv-gallery-next
              aria-label="Наступне фото"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-dark/60 backdrop-blur-sm text-white hover:text-gold border border-white/20 hover:border-gold flex items-center justify-center transition-colors cursor-pointer"
            >
              <ChevronRight className="w-5 h-5 pointer-events-none" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-grv-gallery-dot
                  data-index={i}
                  aria-label={`Фото ${i + 1}`}
                  className={`grv-gallery-dot rounded-full transition-all duration-300 cursor-pointer ${
                    i === 0 ? 'is-active' : ''
                  }`}
                />
              ))}
            </div>
            <div
              data-grv-gallery-counter
              className="absolute top-4 right-4 z-10 bg-dark/60 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/20 pointer-events-none"
            >
              1 / {images.length}
            </div>
          </>
        ) : null}
        <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-gold rounded-tl-xl pointer-events-none" />
      </div>

      <div
        className="grv-gallery-lightbox fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
        data-grv-gallery-lightbox
        hidden
        aria-hidden="true"
      >
        <button
          type="button"
          data-grv-gallery-lb-close
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center text-foreground hover:border-gold hover:text-gold transition-colors cursor-pointer"
          aria-label="Закрити"
        >
          <span className="pointer-events-none text-xl leading-none">×</span>
        </button>
        {images.length > 1 ? (
          <>
            <button
              type="button"
              data-grv-gallery-lb-prev
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center text-foreground hover:border-gold hover:text-gold transition-colors cursor-pointer"
              aria-label="Попереднє"
            >
              <ChevronLeft className="w-5 h-5 pointer-events-none" />
            </button>
            <button
              type="button"
              data-grv-gallery-lb-next
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center text-foreground hover:border-gold hover:text-gold transition-colors cursor-pointer"
              aria-label="Наступне"
            >
              <ChevronRight className="w-5 h-5 pointer-events-none" />
            </button>
          </>
        ) : null}
        <div className="max-w-5xl w-full max-h-[90vh] flex flex-col items-center gap-3">
          <img
            data-grv-gallery-lb-img
            alt=""
            className="max-h-[80vh] w-full object-contain rounded-xl"
          />
          <p className="text-white/70 text-sm" data-grv-gallery-lb-caption />
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ section?: import('@/types/cms').LinkedSectionPageSection }} props
 */
export default function LinkedSection({ section = {} }) {
  const align = section.align === 'right' ? 'right' : 'left';
  const mediaType = section.media_type === 'gallery' ? 'gallery' : 'video';
  const showStats = section.show_stats === true;
  const subTitle = section.sub_title || '';
  const headingLine1 = section.heading_line1 || section.title || '';
  const headingHighlight = section.heading_highlight || '';
  const HeadingTag = section.heading_level === 'h3' ? 'h3' : 'h2';
  const intro = section.intro || '';
  const bullets = section.bullets?.length ? section.bullets : [];
  const stats = showStats && section.stats?.length ? section.stats : [];
  const videoUrl = section.video_url || '';
  const gallery = section.gallery?.length ? section.gallery : [];

  const primaryLabel = (section.primary_button_label || '').trim();
  const primaryHref = section.primary_button_href || '/';
  const secondaryLabel = (section.secondary_button_label || '').trim();
  const secondaryHref = section.secondary_button_href || '/contacts';
  const showPrimary = Boolean(primaryLabel);
  const showSecondary = Boolean(secondaryLabel);

  const mediaOrder =
    align === 'right' ? 'order-first lg:order-last' : 'order-first lg:order-first';
  const textOrder =
    align === 'right' ? 'order-last lg:order-first' : 'order-last lg:order-last';

  const hasHighlight = Boolean(headingHighlight);
  const headingClass = hasHighlight
    ? 'text-4xl sm:text-5xl lg:text-[3.25rem] font-black text-foreground leading-[1.05] mb-5'
    : 'text-3xl sm:text-4xl font-black text-foreground mb-8';

  return (
    <section className="bg-background py-20 lg:py-28 overflow-hidden border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {subTitle ? (
          <div className="flex items-center gap-3 mb-10">
            <div className="w-8 h-px bg-gold" />
            <span className="text-gold text-xs font-semibold uppercase tracking-widest">{subTitle}</span>
          </div>
        ) : null}

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-12 items-center">
          <div className={`w-full lg:w-1/2 shrink-0 ${mediaOrder}`}>
            {mediaType === 'gallery' ? (
              <SectionGallery images={gallery} />
            ) : (
              <SectionVideo videoUrl={videoUrl} />
            )}
          </div>

          <div className={`w-full lg:w-1/2 flex flex-col gap-8 text-center lg:text-left ${textOrder}`}>
            <div>
              {hasHighlight ? (
                <HeadingTag className={headingClass}>
                  {headingLine1}
                  {headingLine1 ? <br /> : null}
                  <span className="text-gold-gradient">{headingHighlight}</span>
                </HeadingTag>
              ) : (
                <HeadingTag className={headingClass}>{headingLine1}</HeadingTag>
              )}

              {intro ? (
                <>
                  <div className="w-14 h-0.5 bg-gold-gradient mb-6 mx-auto lg:mx-0" />
                  <p className="text-muted-foreground text-base lg:text-lg leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
                    {intro}
                  </p>
                </>
              ) : null}
            </div>

            {bullets.length ? (
              <ul className={`space-y-3 mb-10 ${hasHighlight ? '' : 'text-left'}`}>
                {bullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-gold" />
                    </div>
                    <span className="text-foreground text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            {showStats && stats.length ? (
              <div className="grid grid-cols-3 gap-4 py-6 border-t border-b border-border mb-8">
                {stats.map((stat) => (
                  <div key={`${stat.value}-${stat.label}`}>
                    <div className="text-2xl font-black text-gold">{stat.value}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {showPrimary || showSecondary ? (
              <div className="flex gap-4 flex-col sm:flex-row lg:flex-nowrap">
                {showPrimary ? (
                  <AppLink
                    href={primaryHref}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gold-gradient text-dark font-bold px-7 py-3.5 rounded hover:opacity-90 transition-opacity text-sm uppercase tracking-wider"
                  >
                    {primaryLabel} <ArrowRight className="w-4 h-4" />
                  </AppLink>
                ) : null}
                {showSecondary ? (
                  <AppLink
                    href={secondaryHref}
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-border text-foreground hover:border-gold hover:text-gold font-semibold px-7 py-3.5 rounded transition-colors text-sm uppercase tracking-wider"
                  >
                    {secondaryLabel}
                  </AppLink>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
